import { postDislike, postLike } from "@/api/posts";
import HeartAnimation from "@/components/custom-framer-animations/heart-animation";
import H6 from "@/components/ui/headings/H6";
import Iconify from "@/components/ui/Iconify";
import PostAvatar from "@/components/ui/PostAvatar";
import Span from "@/components/ui/Span";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { usePathname } from "next/navigation";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { getIdentifier } from "@/utils/helpers";
import { EVENT_TYPES, sendAnalyticsEvent } from "@/utils/AnalyticsEvents";
import { getMediaRecordPath, getMediaUrl } from "@/utils/media";
import StaticVideoPreview from "@/components/ui/StaticVideoPreview";

const ERROR_MESSAGE = "오류가 발생했습니다.";
const STICKY_TEXT = "고정됨";
const VISIBLE_TO_ME_TEXT = "나만 볼 수 있음";
const FALLBACK_PLACEHOLDER =
  "data:image/jpeg;base64,/9j/2wBDAFA3PEY8MlBGQUZaVVBfeMiCeG5uePWvuZHI////////////////////////////////////////////////////2wBDAVVaWnhpeOuCguv/////////////////////////////////////////////////////////////////////////wAARCAAKABQDASIAAhEBAxEB/8QAFgABAQEAAAAAAAAAAAAAAAAAAwAE/8QAHhAAAgEDBQAAAAAAAAAAAAAAAAECAwQREyHBQoH/xAAVAQEBAAAAAAAAAAAAAAAAAAAAAf/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AMvItvGM54kgRbZtVorOzCtmjSXSPhCkEf/Z";
const DEFAULT_MEDIA_FRAME_CLASS =
  "w-full aspect-[5/7] md:aspect-[2/3] lg:aspect-[3/4] bg-black/5 rounded-t-[6px] rounded-b-none md:rounded-xl overflow-hidden relative md:border md:border-black/[0.08] md:dark:border-white/[0.14]";
const PLAY_FEED_MEDIA_FRAME_BASE_CLASS =
  "w-full bg-[#f3f3f3] rounded-t-[6px] rounded-b-none md:rounded-xl overflow-hidden relative md:border md:border-black/[0.08] md:dark:border-white/[0.14]";
const CARD_MEDIA_CLASS =
  "block h-full w-full object-cover object-center cursor-pointer";
const PLAY_FEED_TITLE_CLASS =
  "line-clamp-2 !text-[16px] !leading-[1.32] !font-medium md:!text-[14px] md:!leading-[1.35] md:!font-medium";
const DEFAULT_TITLE_CLASS =
  "line-clamp-2 !text-[16px] !leading-[1.32] !font-medium md:truncate md:!text-base";
const PLAY_FEED_AUTHOR_CLASS =
  "hover:text-gray-700 dark:!text-dark_secondary_label dark:hover:!text-dark_primary_label !truncate text-nowrap overflow-clip !text-[13px] !font-normal !text-black/60 md:!text-[12px] md:!font-normal w-16 md:w-fit";
const DEFAULT_AUTHOR_CLASS =
  "hover:text-gray-700 dark:!text-dark_secondary_label dark:hover:!text-dark_primary_label !truncate text-nowrap overflow-clip !text-[13px] !font-normal !text-black/45 md:!text-sm md:!font-medium w-16 md:w-fit";
const CLOSURE_RISK_BADGE_CLASS =
  "inline-flex w-fit max-w-full items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-1 text-[11px] font-semibold leading-none text-orange-700 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-100";
const CONFIRMED_CLOSED_BADGE_CLASS =
  "inline-flex w-fit max-w-full items-center gap-1 rounded-full border border-red/30 bg-red/10 px-2 py-1 text-[11px] font-semibold leading-none text-red";

const GENERIC_VIDEO_POSTER_MARKERS = [
  "/images/main-logo",
  "main-logo.png",
  "/camera-icon",
  "camera-icon.png",
  "/no-data",
  "no-data.png",
];

const getVideoPosterSrcSet = (url) => {
  if (!url || !/\/thumbnail\.webp(?:[?#]|$)/i.test(url)) return undefined;

  const mobilePosterUrl = url.replace(
    /\/thumbnail\.webp(?=([?#]|$))/i,
    "/thumbnail_mobile.webp"
  );

  return `${mobilePosterUrl} 420w, ${url} 640w`;
};

const isGenericVideoPosterPath = (path) => {
  if (!path || typeof path !== "string") return false;
  const normalizedPath = path.toLowerCase();
  return GENERIC_VIDEO_POSTER_MARKERS.some((marker) =>
    normalizedPath.includes(marker)
  );
};

const getPostPosterPath = (post) => {
  const media = post?.media?.[0];
  const cover = post?.postMediaCover?.[0];

  return (
    cover?.thumbnailPath ||
    cover?.mediumPath ||
    cover?.coverPath ||
    media?.thumbnailPath ||
    media?.placeholderPath ||
    media?.placeholder ||
    media?.mediumPath ||
    ""
  );
};

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const getFirstDimension = (media, keys) => {
  for (const key of keys) {
    const value = key
      .split(".")
      .reduce((current, part) => current?.[part], media);
    const number = toPositiveNumber(value);
    if (number) return number;
  }
  return null;
};

// 샤오홍슈와 같은 규격: 카드 높이를 **정해진 세 가지**로만 만든다.
//   세로 3:4 · 정사각 1:1 · 가로 4:3
// 예전에는 0.75~1.333 사이 아무 값이나 나와 높이가 제각각이었다.
// 가장 가까운 규격으로 붙인다(0.8 짜리 사진 → 3:4).
//
// 값은 올릴 때 저장해 둔 가로·세로 치수로 그릴 때 계산한다 —
// 사진이 다 받아진 뒤에 재기는 방식은 쓰지 않는다(그러면 배치가 우르르 밀린다).
// 사진을 언제 받고 언제 놓을지.
// 들이는 문턱(900)과 놓는 문턱(3,000)을 다르게 둔다 — 같으면 경계에서 깜빡인다.
const MEDIA_LOAD_MARGIN_PX = 900;
const MEDIA_RELEASE_MARGIN_PX = 3000;

const PLAY_FEED_RATIO_STEPS = [3 / 4, 1, 4 / 3]; // 세로 · 정사각 · 가로
const PLAY_FEED_DEFAULT_RATIO = 3 / 4; // 치수를 모를 때는 세로

const getPlayFeedMediaRatio = (media) => {
  if (!media || typeof media !== "object") return PLAY_FEED_DEFAULT_RATIO;

  const width = getFirstDimension(media, [
    "width",
    "originalWidth",
    "mediaWidth",
    "imageWidth",
    "videoWidth",
    "metadata.width",
    "meta.width",
    "dimensions.width",
  ]);
  const height = getFirstDimension(media, [
    "height",
    "originalHeight",
    "mediaHeight",
    "imageHeight",
    "videoHeight",
    "metadata.height",
    "meta.height",
    "dimensions.height",
  ]);

  if (!width || !height) return PLAY_FEED_DEFAULT_RATIO;

  const ratio = width / height;
  if (!Number.isFinite(ratio) || ratio <= 0) return PLAY_FEED_DEFAULT_RATIO;

  // 가장 가까운 규격으로 붙인다
  return PLAY_FEED_RATIO_STEPS.reduce((best, step) =>
    Math.abs(step - ratio) < Math.abs(best - ratio) ? step : best
  );
};

const ContentMasonryCard = ({
  post,
  setAllPosts,
  onClick,
  href,
  showSticky = false,
  variant = "default",
  priority = false,
}) => {
  const pathname = usePathname();
  const isPlayFeedVariant = variant === "play-feed";
  const primaryMedia = post?.media?.[0];
  const [isFinalMediaLoaded, setIsFinalMediaLoaded] = useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
  const [shouldLoadMedia, setShouldLoadMedia] = useState(
    !isPlayFeedVariant || priority
  );
  const mediaFrameRef = useRef(null);
  const finalMediaImgRef = useRef(null);
  const previewImgRef = useRef(null);
  const previewToFinalTimerRef = useRef(null);
  const { user, openLoginModal } = useAuthContext();
  const [hasUserLikedThisPost, setHasUserLikedThisPost] = useState(
    !!post?.like?.find((_l) => _l?.userId === user?.userId)
  );

  const likePost = async (postId, userId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      if (!userId || !postId) return;
      await postLike(postId, {
        userId,
        contentType: "Post",
      });
    } catch (error) {
      enqueueSnackbar(
        error?.data?.response?.message ||
        error?.message ||
        ERROR_MESSAGE,
        { variant: "error" }
      );
    }
  };

  const dislikePost = async (postId, userId) => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      if (!userId || !postId) return;
      await postDislike(postId, {
        userId,
        contentType: "Post",
      });
    } catch (error) {
      enqueueSnackbar(
        error?.data?.response?.message ||
        error?.message ||
        ERROR_MESSAGE,
        { variant: "error" }
      );
    }
  };

  const handleHeartClick = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    const isLiked = !!post?.like?.find((_l) => _l?.userId === user?.userId);
    if (isLiked) {
      setHasUserLikedThisPost(false);
      setAllPosts((prev) =>
        prev.map((_f) => {
          if (_f.id === post.id) {
            return {
              ..._f,
              like: _f.like.filter((like) => like.userId !== user?.userId),
            };
          }
          return _f;
        })
      );
      dislikePost(post?.id, user?.userId);
    } else {
      setHasUserLikedThisPost(true);
      setAllPosts((prev) =>
        prev.map((_f) => {
          if (_f.id === post.id) {
            return {
              ..._f,
              like: [..._f.like, { userId: user?.userId }],
            };
          }
          return _f;
        })
      );
      likePost(post?.id, user?.userId);
      if (post?.userId !== user?.userId) {
        sendAnalyticsEvent(EVENT_TYPES.interactions, post?.userId);
      }
    }
  };

  const openProfile = (identifier) => {
    if (!identifier) return;
    window.open(identifier, "_blank");
  };

  useEffect(() => {
    if (post && user?.userId) {
      setHasUserLikedThisPost(
        !!post?.like?.find((_l) => _l?.userId === user?.userId)
      );
    }
  }, [post, user]);

  useEffect(() => {
    if (previewToFinalTimerRef.current) {
      clearTimeout(previewToFinalTimerRef.current);
    }
    setIsFinalMediaLoaded(false);
    setIsPreviewLoaded(false);
  }, [
    // 사진을 놓아 줬다가 다시 받을 때도 처음부터(흐린 판 → 진짜 사진) 가야 한다
    shouldLoadMedia,
    post?.id,
    primaryMedia?.id,
    primaryMedia?._id,
    primaryMedia?.filePath,
    primaryMedia?.width,
    primaryMedia?.height,
  ]);

  useEffect(() => {
    if (!isPlayFeedVariant || priority) {
      setShouldLoadMedia(true);
      return;
    }

    const node = mediaFrameRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setShouldLoadMedia(true);
      return;
    }

    setShouldLoadMedia(false);

    // 가까워지면 받는다
    const loader = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setShouldLoadMedia(true);
      },
      { rootMargin: `${MEDIA_LOAD_MARGIN_PX}px 0px`, threshold: 0.01 }
    );
    // 아주 멀어지면 놓아 준다 (사진 메모리가 끝없이 쌓이지 않게)
    const releaser = new IntersectionObserver(
      (entries) => {
        if (entries.every((entry) => !entry.isIntersecting)) setShouldLoadMedia(false);
      },
      { rootMargin: `${MEDIA_RELEASE_MARGIN_PX}px 0px`, threshold: 0 }
    );

    loader.observe(node);
    releaser.observe(node);
    return () => {
      loader.disconnect();
      releaser.disconnect();
    };
  }, [isPlayFeedVariant, priority, post?.id, post?.media?.[0]?.id]);

  useEffect(
    () => () => {
      if (previewToFinalTimerRef.current) {
        clearTimeout(previewToFinalTimerRef.current);
      }
    },
    []
  );

  function getPostMediaUrl(path) {
    if (!path) return "";
    return getMediaUrl(path);
  }

  if (!post?.isPublic && user?.userId !== post?.user?.id) {
    return null;
  }

  const isVideoMedia = primaryMedia?.mediaType?.includes("video");
  const imagePath = getMediaRecordPath(primaryMedia, {
    variant: isPlayFeedVariant ? "thumbnail" : "medium",
  });
  const videoUrl = getPostMediaUrl(primaryMedia?.filePath);
  const posterPath = isVideoMedia ? getPostPosterPath(post) : "";
  const posterUrl =
    posterPath && !isGenericVideoPosterPath(posterPath)
      ? getMediaUrl(posterPath)
      : "";
  const finalMediaUrl = isVideoMedia ? posterUrl : getPostMediaUrl(imagePath);
  const previewPath =
    primaryMedia?.placeholderPath ||
    primaryMedia?.placeholder ||
    (isVideoMedia ? posterPath : "");
  const previewUrl =
    previewPath && !isGenericVideoPosterPath(previewPath)
      ? getMediaUrl(previewPath)
      : "";
  const isPreviewSameAsFinal = Boolean(
    previewUrl && finalMediaUrl && previewUrl === finalMediaUrl
  );
  const videoPosterSrcSet = isVideoMedia
    ? getVideoPosterSrcSet(finalMediaUrl)
    : undefined;
  const mediaSizes = isPlayFeedVariant
    ? "(max-width: 767px) 50vw, 20vw"
    : undefined;
  const showPreviewOverlay = Boolean(
    shouldLoadMedia && previewUrl && !isFinalMediaLoaded
  );
  const showSkeletonOverlay =
    !shouldLoadMedia || (!isPreviewLoaded && !isFinalMediaLoaded);
  const mediaLoading = priority ? "eager" : "lazy";
  const mediaFetchPriority = priority ? "high" : undefined;
  const mediaFrameClass = isPlayFeedVariant
    ? PLAY_FEED_MEDIA_FRAME_BASE_CLASS
    : DEFAULT_MEDIA_FRAME_CLASS;
  // Continuous clamped ratio known from stored dims at first render (props),
  // applied inline — no layout shift, no on-load setState, no relayout cascade.
  const mediaFrameStyle = isPlayFeedVariant
    ? { aspectRatio: String(getPlayFeedMediaRatio(primaryMedia)) }
    : undefined;
  const closureRisk =
    post?.localdataClosureRisk || post?.place?.localdataClosureRisk || null;
  const showClosureRisk = Boolean(closureRisk?.publicWarning);
  const closureRiskLabel =
    closureRisk?.label ||
    (closureRisk?.level === "CONFIRMED_CLOSED"
      ? "폐업 확인"
      : "영업 재확인 중");
  const closureRiskBadgeClass =
    closureRisk?.level === "CONFIRMED_CLOSED"
      ? CONFIRMED_CLOSED_BADGE_CLASS
      : CLOSURE_RISK_BADGE_CLASS;

  // 작성자 3분기 (place-detail 리디자인 S3, 2026-07-30 확정):
  //   1) 공식 매장 계정 → 로고를 라운드 사각으로 + ✓공식 배지 (개인과 시각적으로
  //      구분되어야 한다: 매장 로고는 원형으로 자르면 브랜드가 깨진다)
  //   2) 일반 유저     → 원형 아바타 (기존 동작 그대로)
  //   3) 작성자 없음   → 아무것도 렌더하지 않는다. 예전에는 이 경우에도 기본
  //      아바타(/person.png) + 빈 이름이 나와서 "이름 없는 유령 작성자"처럼 보였다.
  //      작성자가 지워진 포스트(현재 공개 포스트 100건 중 12건이 그런 상태다)나
  //      raw 사진처럼 사람이 없는 항목이 여기에 해당한다.
  // 매장 계정 시스템은 아직 없어서 (1)은 데이터가 붙기 전까지 도달하지 않는다.
  const storeAuthor = post?.storeAccount?.isOfficial ? post.storeAccount : null;
  const userAuthor = post?.user?.id ? post.user : null;
  const hasAuthor = Boolean(storeAuthor || userAuthor);
  const handleFinalMediaLoad = () => {
    if (!isPreviewSameAsFinal) {
      setIsFinalMediaLoaded(true);
    }
  };
  const handlePreviewLoad = () => {
    setIsPreviewLoaded(true);
    if (isPreviewSameAsFinal) {
      if (previewToFinalTimerRef.current) {
        clearTimeout(previewToFinalTimerRef.current);
      }
      previewToFinalTimerRef.current = setTimeout(() => {
        setIsFinalMediaLoaded(true);
      }, 180);
    }
  };

  // 서버가 그려 보낸 사진은 React 가 붙기 전에 **이미 다 받아져 있을 수 있다.**
  // 그러면 onLoad 가 다시 울리지 않아 흐린 판이 영영 안 걷힌다.
  // 붙는 순간 한 번 직접 확인한다. (서버 렌더링을 켠 2026-08-15 에 필요해졌다)
  useEffect(() => {
    const done = (node) => node && node.complete && node.naturalWidth > 0;
    if (done(previewImgRef.current)) handlePreviewLoad();
    if (done(finalMediaImgRef.current)) handleFinalMediaLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadMedia, finalMediaUrl, previewUrl]);

  // 껍데기가 진짜 링크(<a href>)일 때의 누름 처리.
  // ⚠️ 특수 누름(⌘/Ctrl/Shift/Alt)은 **막지 않는다** — 그게 "새 탭으로 열기"다.
  // 평소 누름만 기본 이동을 막고, 예전부터 하던 일(보던 목록 기억 → 창/이동)을 그대로 한다.
  const handleCardActivate = (event) => {
    if (href) {
      if (event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey) return;
      event?.preventDefault?.();
    }
    onClick?.(event);
  };
  // ⚠️ 링크(<a>)는 기본이 인라인이라, block 을 같이 안 주면 w-full 이 안 먹어 칸이 무너진다.
  const MediaFrameTag = href ? "a" : "div";

  return (
    <div className="flex flex-col gap-1.5 md:gap-2 w-full">
      <MediaFrameTag
        ref={mediaFrameRef}
        className={href ? `${mediaFrameClass} block` : mediaFrameClass}
        style={mediaFrameStyle}
        onClick={handleCardActivate}
        {...(href ? { href } : {})}
      >
        <div
          className={`absolute inset-0 bg-[#f4f4f4] dark:bg-white/[0.08] transition-opacity duration-200 ease-out ${
            showSkeletonOverlay ? "opacity-100 animate-pulse" : "opacity-0"
          }`}
        />
        {shouldLoadMedia && primaryMedia?.mediaType?.includes("livephoto") ? (
          <div className="w-full h-full cursor-pointer">
            <img
              src={getPostMediaUrl(post?.media?.[0]?.livePhotoImagePath)}
              alt="live image"
              className={`${CARD_MEDIA_CLASS} relative z-[1] transition-opacity duration-300 ease-out ${
                isFinalMediaLoaded ? "opacity-100" : "opacity-0"
              }`}
              loading={mediaLoading}
              fetchPriority={mediaFetchPriority}
              decoding="async"
              ref={finalMediaImgRef}
            onLoad={handleFinalMediaLoad}
            />
          </div>
        ) : shouldLoadMedia && primaryMedia?.mediaType?.includes("image") ? (
          <img
            src={finalMediaUrl}
            alt="post"
            className={`${CARD_MEDIA_CLASS} relative z-[1] transition-opacity duration-300 ease-out ${
              isFinalMediaLoaded ? "opacity-100" : "opacity-0"
            }`}
            loading={mediaLoading}
            fetchPriority={mediaFetchPriority}
            decoding="async"
            onClick={handleCardActivate}
            ref={finalMediaImgRef}
            onLoad={handleFinalMediaLoad}
          />
        ) : shouldLoadMedia && finalMediaUrl ? (
          <img
            src={finalMediaUrl}
            alt="video preview"
            className={`${CARD_MEDIA_CLASS} relative z-[1] transition-opacity duration-300 ease-out ${
              isFinalMediaLoaded ? "opacity-100" : "opacity-0"
            }`}
            draggable={false}
            loading={mediaLoading}
            fetchPriority={mediaFetchPriority}
            decoding="async"
            srcSet={videoPosterSrcSet}
            sizes={videoPosterSrcSet ? mediaSizes : undefined}
            onClick={handleCardActivate}
            ref={finalMediaImgRef}
            onLoad={handleFinalMediaLoad}
          />
        ) : shouldLoadMedia ? (
          <StaticVideoPreview
            src={videoUrl}
            className={`${CARD_MEDIA_CLASS} relative z-[1] transition-opacity duration-300 ease-out ${
              isFinalMediaLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoadedData={() => {
              setIsFinalMediaLoaded(true);
            }}
          />
        ) : null}
        {shouldLoadMedia && previewUrl && (
          <div
            className={`absolute inset-0 z-[2] pointer-events-none overflow-hidden transition-opacity duration-300 ease-out ${
              showPreviewOverlay ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={previewUrl || FALLBACK_PLACEHOLDER}
              alt="post-card-media"
              className="h-full w-full scale-[1.06] object-cover blur-xl"
              loading={mediaLoading}
              fetchPriority={mediaFetchPriority}
              decoding="async"
              ref={previewImgRef}
              onLoad={handlePreviewLoad}
            />
            <div className="absolute inset-0 bg-white/[0.02]" />
          </div>
        )}
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/0 cursor-pointer" />
        {post?.pinnedPosts?.find((_pf) => _pf?.postId === post?.id) &&
          showSticky && (
            <div className="absolute top-1.5 left-1.5 w-fit bg-red text-white z-10 text-[12px] flex items-center justify-center gap-1 py-1 px-3 rounded-full text-shadow-xl">
              {STICKY_TEXT}
            </div>
          )}
        {!post?.isPublic && user?.userId && user?.userId === post?.user?.id && (
          <div className="absolute bottom-1.5 left-1.5 w-fit bg-black bg-opacity-50 backdrop-blur-md text-white z-10 text-[11px] flex items-center justify-center gap-1 py-1 px-3 rounded-full text-shadow-xl">
            <Iconify
              icon={"material-symbols:lock"}
              className={"!text-white !w-3.5 !h-3.5"}
            />{" "}
            {VISIBLE_TO_ME_TEXT}
          </div>
        )}
        {post?.media && post?.media[0]?.mediaType?.includes("video") && (
          <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2 z-10 w-5 h-5 rounded-full bg-black bg-opacity-30 backdrop-blur-2xl flex items-center justify-center">
            <Iconify
              icon={"mynaui:play-solid"}
              className={"!text-white !w-4 !h-4"}
            />
          </div>
        )}
      </MediaFrameTag>
      <div className="w-full px-1.5 md:px-2 flex flex-col gap-1.5 md:gap-2">
        <H6
          className={isPlayFeedVariant ? PLAY_FEED_TITLE_CLASS : DEFAULT_TITLE_CLASS}
        >
          {post?.title}
        </H6>
        {showClosureRisk && (
          <div className={closureRiskBadgeClass}>
            <Iconify
              icon="mdi:alert-circle-outline"
              className="!h-3.5 !w-3.5 !min-w-3.5 !text-inherit"
            />
            <span className="truncate">{closureRiskLabel}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          {storeAuthor ? (
            <div className="flex min-w-0 items-center gap-1 sm:gap-2">
              <span className="relative h-5 w-5 flex-none overflow-hidden rounded-[6px] bg-black/[0.06] dark:bg-white/[0.1]">
                {storeAuthor?.logoUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={storeAuthor.logoUrl}
                    alt={`${storeAuthor?.name || "매장"} 로고`}
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </span>
              <Span
                className={isPlayFeedVariant ? PLAY_FEED_AUTHOR_CLASS : DEFAULT_AUTHOR_CLASS}
              >
                {storeAuthor?.name}
              </Span>
              <Iconify
                icon={"material-symbols:verified-rounded"}
                className={"!h-3.5 !w-3.5 !min-w-3.5 !text-[#2f80ed]"}
                aria-label="공식 매장 계정"
              />
            </div>
          ) : userAuthor ? (
            <div
              className="flex min-w-0 items-center gap-1 sm:gap-2 cursor-pointer"
              onClick={() =>
                openProfile(getIdentifier(userAuthor?.username, userAuthor?.id))
              }
            >
              <PostAvatar src={userAuthor?.profilePicture} />
              <Span
                id={userAuthor?.id}
                className={isPlayFeedVariant ? PLAY_FEED_AUTHOR_CLASS : DEFAULT_AUTHOR_CLASS}
              >
                {userAuthor?.name}
              </Span>
            </div>
          ) : null}
          <div
            className={`group flex items-center gap-1 cursor-pointer text-xs select-none ${
              hasAuthor ? "" : "ml-auto"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleHeartClick();
            }}
          >
            <HeartAnimation hasClicked={hasUserLikedThisPost} size={18} />
            {post?.like?.length > 0 ? post?.like?.length : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentMasonryCard;
